import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import Paper from "@material-ui/core/Paper";
import { BASIC_SPECS_SECTIONS } from "../../redux_store/jobState_Schemas";
import { SHOW_DESIGNER_MESSAGE, CUSTOM_COMPONENT_LIST_IS_OPEN, updateDesigner } from "../../redux_store/appState";
import * as CONFIG from "../../redux_store/configState";
import { BaseURL, deepCopy } from "../../components/Common";
import MessageSupport from "./MessageSupport";
import BSL_Dialog from "../../components/BSL_Dialog";
import BSL_TextInput from "../../components/BSL_TextInput";
import BracketDesigner from "./BracketDesigner";
import { GetShapeUpdateString, GetColourUpdateString, GetBOMUpdateString } from "../layout/PaperFunctionHelper";
import { DStateActive, uploadBackup } from "../../redux_store/appStateHelper";
import { addNewHistoryLine } from "../../helper/Helper";
import { pricing_data_template, fielders_cust_types, special_factors_default } from "../../redux_store/configState";
import {
    updateBOMData,
    updateBOMOrderData,
    updateBOMFreightPOA,
    updateJobHistory,
    updateBOMOrderDataToCustomerOrder,
    getPastOrderLists,
} from "../../redux_store/jobState";

export function normaliseFreightPOA(data) {
    if (!data || typeof data !== "object") return {};
    const normalisedData = {};

    Object.keys(data).forEach((key) => {
        if (typeof data[key] === "object" && data[key] !== null) {
            normalisedData[key] = {
                Price: typeof data[key].Price === "number" ? data[key].Price : Number(data[key].Price) || 0,
                Invoice: data[key].Invoice || "TBD",
            };
        } else {
            normalisedData[key] = {
                Price: typeof data[key] === "number" ? data[key] : Number(data[key]) || 0,
                Invoice: "TBD",
            };
        }
    });

    return normalisedData;
}

export default function BOMFrame(props) {
    const dispatch = useDispatch();
    const { toggleBomOpen, toggleDock, isPopOut, fast } = props;

    const pricefile_default = useSelector((state) => state.app.pricefile);
    const colour_map = useSelector((state) => state.config?.colour_map);
    const colour_arrays = useSelector((state) => state.config?.colour_arrays);
    const dist_levels = useSelector((state) => state.app.dist_levels);
    const special_factors = useSelector((state) => state.app.special_factors);
    const UNLOCKED = useSelector((state) => state.app.designer.UNLOCKED);
    const user = useSelector((state) => state.app.user);
    const job = useSelector((state) => state.job);
    const app = useSelector((state) => state.app);
    const config = useSelector((state) => state.config);

    const [pricefile, setPricefile] = useState(pricefile_default);
    const [designBracketMode, setDesignBracketMode] = useState("");
    const [editorItemMode, setEditorItemMode] = useState(null);
    const [bracketData, setBracketData] = useState(null);
    const [emailContentFreightPOA, setEmailContentFreightPOA] = useState("");
    const [poaEmailSubject, setPoaEmailSubject] = useState("");
    const [bubbleSetup, setBubbleSetup] = useState(false);
    const [showDialogFreightPOA, setShowDialogFreightPOA] = useState(false);
    const [showEmailFreightPOA, setShowEmailFreightPOA] = useState(false);
    const [targetProductFreightPOA, setTargetProductFreightPOA] = useState("");
    const [poaItem, setPOAItem] = useState(false);
    const [newFreightPOA, setNewFreightPOA] = useState(null);
    const [apiCallCount, setAPICallCount] = useState(0);

    const [matMgrReady, setMatMgrReady] = useState(false);
    const popOutRef = useRef(null);
    const iframeRef = useRef(null);
    const windowRef = useRef(null);
    const isPopOutRef = useRef(isPopOut || false);
    const sessionId = useRef(crypto.randomUUID());

    const carportMode = job?.DesignParameters?.CarportMode === 1;
    const quoteParamsFreightPOA = job?.QuoteParameters?.BOM?.FreightPOA || {};
    const hashNow = GetShapeUpdateString() + GetColourUpdateString() + GetBOMUpdateString();
    const hashAfterLastBOMUpdate = job?.QuoteParameters?.BOM?.bomHashCode;

    const matMgrOrigin = useMemo(() => {
        try {
            return new URL(BaseURL("/")).origin;
        } catch {
            return window.location.origin;
        }
    }, []);

    useEffect(() => {
        // Cleanup on unmount
        return () => {
            dispatch(updateDesigner(CUSTOM_COMPONENT_LIST_IS_OPEN, false));

            if (typeof toggleDock === "function") {
                toggleDock(true);
            }

            if (popOutRef.current && !popOutRef.current.closed) {
                try {
                    popOutRef.current.postMessage({ type: "parentClosed" }, matMgrOrigin);
                    popOutRef.current.close();
                } catch { }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        dispatch(
            updateBOMData(job, true, () => {
                setAPICallCount((c) => c + 1);
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, hashNow, app?.pricefile_tier, job?.Customer?.DistLevel]);

    useEffect(() => {
        if (apiCallCount > 0) {
            if (matMgrReady) {
                sendValue("loadingSystemList", true);
                sendValue("onlyGetPricing", fast);
                setDisplayOptions();
                setPriceFileData();
                setMiscellaneousData();
                setQuoteData();
                setDisplayOptions();
                setBOMData();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiCallCount, matMgrReady, fast]);

    useEffect(() => {
        if (matMgrReady) {
            setDisplayOptions();
            setBOMData();
            sendValue("onlyGetPricing", fast);
            sendValue("loadingSystemList", false);
        }
    }, [job?.QuoteParameters?.BOM?.OrderList, job?.QuoteParameters?.BOM?.PastOrderLists]);

    const refreshWindowRef = useCallback(() => {
        windowRef.current = isPopOutRef.current
            ? popOutRef.current
            : iframeRef.current
                ? iframeRef.current.contentWindow
                : null;
    }, []);

    useEffect(() => {
        const beforeUnload = () => postToMatMgr({ type: "parentClosed" });
        window.addEventListener("beforeunload", beforeUnload);
        return () => window.removeEventListener("beforeunload", beforeUnload);
    }, [postToMatMgr]);

    useEffect(() => {
        function onMessage(event) {
            if (event.origin !== matMgrOrigin) return;
            const msg = event.data || {};

            if (msg.session && msg.session !== sessionId.current) return;
            const { type } = msg;

            switch (type) {
                case "ready": {
                    setMatMgrReady(true);
                    break;
                }

                case "thisClose": {
                    const system_list_data = msg.data;
                    if (!system_list_data) {
                        toggleBomOpen(false, true, false);

                        if (isPopOutRef.current && popOutRef.current) {
                            popOutRef.current.close();
                        }

                        return;
                    }

                    system_list_data.pricing_timestamp = Date.now();

                    dispatch(updateBOMOrderData(null, system_list_data)).then(() => toggleBomOpen(false, true, false));
                    if (isPopOutRef.current && popOutRef.current) {
                        popOutRef.current.close();
                    }
                    break;
                }

                case "thisNotifyDesigner": {
                    const { component, status } = msg;
                    dispatch(updateDesigner(component, status));
                    break;
                }

                case "thisRemoveOrderList": {
                    dispatch(updateBOMOrderData({}));
                    break;
                }

                case "thisSave": {
                    const { order_data, system_list_data, closeFrame = false } = msg;

                    if (system_list_data) system_list_data.pricing_timestamp = Date.now();
                    if (order_data) order_data.pricing_timestamp = Date.now();

                    let history = job?.History || [];
                    let changes = [];
                    let addChanges = true;

                    if (order_data?.items?.length) {
                        for (let item of order_data.items) {
                            if (item.deleted || item.manually_added) {
                                if (item.manually_added) changes.push({ ADD: item.Code });
                                if (item.deleted) changes.push({ REMOVE: item.Code });
                            }
                        }

                        for (let j = 0; j < history.length; j++) {
                            const existingChanges = history[j]["Change to BOM"];
                            if (existingChanges && arraysEqual(existingChanges, changes)) {
                                addChanges = false;
                                break;
                            }
                        }

                        if (order_data.items.length > 2) {
                            uploadBackup(job?.id, "Component_List_Backup_", order_data);
                        }
                    }

                    if (addChanges) {
                        let newHistory = addNewHistoryLine({
                            jobState: { history },
                            historyLine: { "Change to BOM": changes },
                        });
                        if (newHistory?.length > 0) history.push(newHistory[0]);

                        const job_id = job?.id;
                        const business_code = job?.BusinessCode;
                        dispatch(updateJobHistory({ business_code, job_id, new_history: history }));
                    }

                    dispatch(updateBOMOrderData(order_data, system_list_data)).then(() => {
                        if (system_list_data) {
                            toggleBomOpen(true, closeFrame, false);
                            if (isPopOutRef.current && popOutRef.current) {
                                popOutRef.current.close();
                            }
                        }
                    });
                    break;
                }

                case "thisEmail": {
                    const { system_items } = msg;
                    handleEmailFreightPOA(system_items);
                    break;
                }

                case "SetFreightPOA": {
                    const { product, details, poa_bom_item } = msg;
                    handleSetFreightPOA(product, details, poa_bom_item);
                    break;
                }

                case "DesignBracket": {
                    const { item, BracketData, page_setup, edit, editor_item_mode, editing_index } = msg;
                    setBracketData({
                        BracketData,
                        item,
                        page_setup,
                        editing_index: editing_index ?? 0,
                    });
                    setDesignBracketMode(edit ? "LimitedEdit" : "View");
                    setEditorItemMode(editor_item_mode);
                    break;
                }

                case "ReloadOrderList": {
                    const { filename } = msg;
                    dispatch(updateBOMOrderDataToCustomerOrder(filename));
                    break;
                }

                case "GetOrderListItems": {
                    dispatch(getPastOrderLists());
                    break;
                }

                case "BackupOrderList": {
                    const { orderList } = msg;
                    uploadBackup(job?.id, "Component_List_Backup_", orderList);
                    break;
                }

                case "ToggleDock": {
                    if (popOutRef.current && !popOutRef.current.closed) return;
                    toggleDock();
                    break;
                }

                default:
                    break;
            }
        }

        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, [matMgrOrigin, dispatch, job, toggleBomOpen, toggleDock, popOutRef]);

    useEffect(() => {
        if (!isPopOutRef.current || matMgrReady) return;

        const handshakeInterval = setInterval(() => {
            if (popOutRef.current && !popOutRef.current.closed) {
                refreshWindowRef();
                postToMatMgr({ type: "hello" });
            } else {
                clearInterval(handshakeInterval);
                setMatMgrReady(false);
            }
        }, 600);

        return () => clearInterval(handshakeInterval);
    }, [matMgrReady, postToMatMgr, refreshWindowRef]);

    useEffect(() => {
        if (!isPopOutRef.current) return;

        const watcher = setInterval(() => {
            if (!popOutRef.current || popOutRef.current.closed) {
                clearInterval(watcher);
                popOutRef.current = null;
                setMatMgrReady(false);
                toggleBomOpen(false, true, true);
            }
        }, 1000);

        return () => clearInterval(watcher);
    }, [isPopOutRef]);

    useEffect(() => {
        if (isPopOutRef.current) {
            openPopOut();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (popOutRef.current && !popOutRef.current.closed) {
                popOutRef.current.postMessage({ type: "parentClosed" }, matMgrOrigin);
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, []);

    useEffect(() => {
        if (!isPopOutRef.current && iframeRef.current && !bubbleSetup) {
            setBubbleSetup(true);
            bubbleIframeMouseMove(iframeRef.current);
        }
    }, [isPopOutRef, bubbleSetup]);

    const postToMatMgr = useCallback(
        (message) => {
            refreshWindowRef();
            const win = windowRef.current;
            if (!win || win.closed) return;
            try {
                win.postMessage({ session: sessionId.current, ...message }, matMgrOrigin);
            } catch { }
        },
        [matMgrOrigin, refreshWindowRef]
    );

    const sendValue = useCallback(
        (parameter, value) => {
            postToMatMgr({ type: "SetVal", parameter, value });
        },
        [postToMatMgr]
    );

    const handleIframeLoad = useCallback(() => {
        refreshWindowRef();
        postToMatMgr({ type: "hello" });
    }, [postToMatMgr, refreshWindowRef]);

    const openPopOut = useCallback(() => {
        if (popOutRef.current && !popOutRef.current.closed) {
            popOutRef.current.focus();
            return;
        }

        const url = `${BaseURL("bom/")}?session=${encodeURIComponent(sessionId.current)}`;
        popOutRef.current = window.open(url, "MaterialManager", "width=1400,height=900,resizable,scrollbars");

        setMatMgrReady(false);
    }, []);

    function setQuoteData() {
        const quoteData = job?.QuoteParameters?.BOM?.QuoteData ?? {};
        const fixedPriceKitsCopy = deepCopy(job?.Distributor?.FixedPriceKits);
        const designTemplateName = job?.AreaSpecifications?.[0]?.SHAPE?.DesignTemplateName;

        if (carportMode && quoteData?.AreaSpecifications?.[0]) {
            quoteData.AreaSpecifications[0]["CarportBoltedFootings"] =
                job?.AreaSpecifications?.[0]?.Footings?.length ?? 0;
        }

        quoteData["PricingData"] = GetPricingFactors();
        quoteData["CURRENT_DEALER"] = quoteData["CURRENT_DEALER"] || {};
        quoteData["CURRENT_DEALER"]["FixedPriceKits"] = fixedPriceKitsCopy || {};
        quoteData["BOMFilter"] =
            config?.design_options?.[job.DesignSummary]?.design_templates?.[designTemplateName]?.bom_items?.filter;

        sendValue("quote_data", quoteData);
    }

    function setBOMData() {
        let systemList = job?.QuoteParameters?.BOM?.SystemList;
        let orderList = job?.QuoteParameters?.BOM?.OrderList;
        const cannotDeleteCodes = app.offerings?.[window.brand]?.Master?.cannot_delete_codes ?? [];

        sendValue("cannotDeleteCodes", cannotDeleteCodes);

        if (systemList?.items?.length > 0) {
            if (DStateActive({ config }) && app?.pricefile?.DMap) {
                systemList.items.forEach((item) => {
                    const lyvesCode = item.Fielders_Code;
                    if (app.pricefile.DMap.hasOwnProperty(lyvesCode)) {
                        item.Fielders_Code = app.pricefile.DMap[lyvesCode];
                    }
                });
            }

            sendValue("freightPOA", job?.QuoteParameters?.BOM?.FreightPOA || {});
            sendValue("system_items", systemList);
            sendValue("loadingSystemList", false);
            sendValue("onlyGetPricing", fast);
        }

        if (orderList?.items) sendValue("order_items", orderList);
        if (job?.QuoteParameters?.BOM?.PastOrderLists) {
            sendValue("past_order_lists", job?.QuoteParameters?.BOM?.PastOrderLists);
            sendValue("loadingSystemList", false);
            sendValue("onlyGetPricing", fast);
        }
    }

    function setPriceFileData() {
        let pricefile_to_load = pricefile;

        if (
            job?.Distributor?.LYVESAccount !== app?.user?.BusinessDetails?.LYVESAccount &&
            job?.Distributor?.Type !== "BBC"
        ) {
            if (app?.pricefile_tier && app?.pricefile_tier?.LYVESAccount === job?.Distributor?.LYVESAccount) {
                pricefile_to_load = app?.pricefile_tier;
            } else {
                toggleBomOpen(false);
                dispatch({
                    type: SHOW_DESIGNER_MESSAGE,
                    message:
                        "Error: Pricing Account has not been loaded for this job.... it may still be loading or there may be an error.",
                });
            }
        }

        sendValue("product_database", pricefile_to_load);
    }

    function setDisplayOptions() {
        const num_order_items = job?.QuoteParameters?.BOM?.OrderList?.items?.length || 0;
        const isStaff = user?.Role >= 90;
        const orderStatus = (job?.OrderStatus || "").toUpperCase();

        const display_options = {
            locked:
                (!isStaff && orderStatus === "PENDING" && !UNLOCKED) ||
                (!isStaff && orderStatus === "ACCEPTED" && !UNLOCKED) ||
                job?.DOrderChanges != null,
            d_state_active: DStateActive({ config }),
            show_prices: false,
            show_eprices: false,
            use_eprice: true,
            role: user.Role,
            expanded_links: false,
            engValid: props?.engValid,
            customise_order_list: num_order_items > 0,
            confirmButton: props.confirmButton || false,
            debug: window.debug || (window.test && user.Role >= 99),
            brand: window.brand,
            retained_percentage: 5,
            use_bom_splicing: job?.DesignParameters?.USE_BOM_SPLICING,
            poa: job?.App?.temp_POA || job?.POA,
            cut_length_extra: job?.SiteParameters?.CONST_BEAM_CUT_EXTRA || (window.LYSAGHT ? 100 : 30),
            order_status: job?.OrderStatus,
            allowPop: typeof toggleDock === "function",
        };

        sendValue("display_options", display_options);
    }

    function setMiscellaneousData() {
        if (colour_map) sendValue("colour_map", colour_map);
        if (colour_arrays) sendValue("colour_arrays", colour_arrays);
        if (job?.DOrderChanges) sendValue("d_item_changes", job.DOrderChanges);
        if (hashAfterLastBOMUpdate === hashNow) {
            sendValue("loadingSystemList", false);
            sendValue("onlyGetPricing", fast);
        }
    }

    function handleEmailFreightPOA(system_items) {
        let email_message = "";
        let subject = "";

        if (system_items?.items) {
            let has_insulated_panels = false;
            let ip_qty = "";

            for (let item of system_items.items) {
                if (item?.Desc?.includes("Insulated") && item?.Category === "STEEL ROOFING") {
                    has_insulated_panels = true;

                    if (item?.Qty) {
                        let qty_summary = "";
                        let split = item?.Qty.split("|");
                        for (let qty of split) {
                            qty_summary += (qty_summary == "" ? "" : ", ") + qty.split("(")[0];
                        }
                        ip_qty += (ip_qty == "" ? "" : "\n") + qty_summary + " of " + item?.Desc;
                    }
                }

                if (
                    has_insulated_panels &&
                    item?.PromptCodes?.includes &&
                    item?.QtyCalculated &&
                    item.PromptCodes.includes("$$=BONDOR_DELIVERY") &&
                    item.QtyCalculated.TotalePriceLastCalculated <= 2
                ) {
                    if (
                        job?.SiteParameters?.Address?.FormattedAddress &&
                        !email_message.includes("Quote For Delivery")
                    ) {
                        email_message +=
                            "Please price Insulated Panel Quote For Delivery to " +
                            job?.SiteParameters?.Address?.FormattedAddress +
                            "\n\n";
                        subject += "Insulated Panel Delivery";
                    }
                    email_message += "Item: " + item["Code"] + " (" + item?.Desc + ")" + "\n";
                    email_message += "Qty & Length: " + ip_qty + "\n";
                }
            }

            for (let item of system_items.items) {
                if (
                    (!quoteParamsFreightPOA[item["Code"]] &&
                        item?.PromptCodes &&
                        item?.QtyCalculated &&
                        item.PromptCodes.includes("$$=POA")) ||
                    item?.QtyCalculated?.TotalePriceLastCalculated > 200000 ||
                    !item?.QtyCalculated?.TotalePriceLastCalculated ||
                    Number.isNaN(item?.QtyCalculated?.TotalePriceLastCalculated)
                ) {
                    if (!email_message.includes("missing prices")) {
                        if (email_message !== "") email_message += "\n";
                        email_message += "Please price the following items, which are missing prices or are POA...\n\n";
                        subject += (subject !== "" ? " & " : "") + "Price Confirmation";
                    }
                    let qtyString = item?.QtyCalculated?.QtyString;
                    email_message +=
                        "Item: " +
                        item?.QtyCalculated?.TotalQty?.() +
                        " x " +
                        item["Code"] +
                        "/" +
                        item["Fielders_Code"] +
                        " (" +
                        item?.Desc +
                        ")" +
                        (item?.Colour ? " in Colour:" + item?.Colour : "") +
                        "\n";
                    if (qtyString?.includes?.("x")) {
                        email_message += "    - " + qtyString.replaceAll("|", ", ") + "\n";
                    }
                }
            }
        }

        if (email_message !== "") {
            subject += " Request for Job #" + job.id + " (" + job?.Customer?.JobName + ")";
        }
        setEmailContentFreightPOA(email_message);
        setPoaEmailSubject(subject);
        setShowEmailFreightPOA(true);
    }

    function handleSetFreightPOA(product, details, poa_bom_item) {
        if (details === undefined) {
            setTargetProductFreightPOA(product);
            setPOAItem(!!poa_bom_item);
            setShowDialogFreightPOA(true);

            if (quoteParamsFreightPOA.hasOwnProperty(product)) {
                const normalisedFreightPOA = normaliseFreightPOA(quoteParamsFreightPOA);
                setNewFreightPOA(normalisedFreightPOA[product]);
            } else {
                setNewFreightPOA({ Price: 0, Invoice: "TBD" });
            }
        } else {
            if (!(quoteParamsFreightPOA.hasOwnProperty(product) && quoteParamsFreightPOA[product] === details)) {
                quoteParamsFreightPOA[product] = details;
                dispatch(updateBOMFreightPOA(quoteParamsFreightPOA));
                sendValue("freightPOA", quoteParamsFreightPOA);
            }
        }
    }

    function arraysEqual(arr1 = [], arr2 = []) {
        if (arr1.length !== arr2.length) return false;
        const a = arr1.slice().sort((x, y) => JSON.stringify(x).localeCompare(JSON.stringify(y)));
        const b = arr2.slice().sort((x, y) => JSON.stringify(x).localeCompare(JSON.stringify(y)));
        for (let i = 0; i < a.length; i++) {
            const o1 = { ...a[i] };
            const o2 = { ...b[i] };
            delete o1.Date;
            delete o2.Date;
            if (JSON.stringify(o1) !== JSON.stringify(o2)) return false;
        }
        return true;
    }

    function bubbleIframeMouseMove(iframe) {
        const cw = iframe.contentWindow;
        if (!cw) return;

        const prevMove = cw.onmousemove;
        cw.onmousemove = function (e) {
            if (prevMove) prevMove(e);
            const evt = document.createEvent("MouseEvents");
            const r = iframe.getBoundingClientRect();
            evt.initMouseEvent(
                "mousemove",
                true,
                false,
                window,
                e.detail,
                e.screenX,
                e.screenY,
                e.clientX + r.left,
                e.clientY + r.top,
                e.ctrlKey,
                e.altKey,
                e.shiftKey,
                e.metaKey,
                e.buttons,
                null
            );
            iframe.dispatchEvent(evt);
        };

        const prevUp = cw.onmouseup;
        cw.onmouseup = function (e) {
            if (prevUp) prevUp(e);
            const evt = document.createEvent("MouseEvents");
            const r = iframe.getBoundingClientRect();
            evt.initMouseEvent(
                "mouseup",
                true,
                false,
                window,
                e.detail,
                e.screenX,
                e.screenY,
                e.clientX + r.left,
                e.clientY + r.top,
                e.ctrlKey,
                e.altKey,
                e.shiftKey,
                e.metaKey,
                e.buttons,
                null
            );
            iframe.dispatchEvent(evt);
        };
    }

    function GetPricingFactors() {
        let factors = pricing_data_template;
        let state =
            job?.Distributor?.Address?.State ||
            user?.BusinessDetails?.Address?.State ||
            job?.SiteParameters?.Address?.State ||
            "NSW";
        if (state === "ACT") state = "NSW";

        let lyves_account = job?.Distributor.LYVESAccount;
        if (
            job?.Customer?.DistLevel &&
            job?.Customer?.DistLevel !== "Retail" &&
            job?.Customer?.DistLevel !== job?.Distributor.LYVESAccount
        ) {
            if (app?.pricefile_tier && app?.pricefile_tier?.LYVESAccount === job?.Customer?.DistLevel) {
                lyves_account = app?.pricefile_tier?.LYVESAccount;
            }
        }

        factors.LYVESAccount = lyves_account;
        factors["overall_markup"] = get_special_factor("OVER ALL MARKUP", state, special_factors);

        for (let areaIndex in job?.AreaSpecifications) {
            factors["area_pricing"][areaIndex] =
                GetFactorForDesignType(job, Number(areaIndex), state) * factors["overall_markup"];
        }

        let dist = "Trade";
        if (job?.Distributor?.DistLevel) dist = job?.Distributor?.DistLevel;
        if (job?.Customer?.DistLevel) dist = job?.Customer?.DistLevel;

        factors["fielders_material_markup_factor"] = get_cust_type(dist, dist_levels);
        factors["fielders_material_markup_factor_type"] = dist;
        factors["fielders_pricing_factors"]["FIELDERS"] = get_special_factor(
            "MATERIALS - FIELDERS",
            state,
            special_factors
        );
        factors["fielders_pricing_factors"]["RHS"] = get_special_factor("MATERIALS - RHS", state, special_factors);
        factors["fielders_pricing_factors"]["SWAGE"] = get_special_factor("MATERIALS - SWAGE", state, special_factors);
        factors["fielders_pricing_factors"]["SLATS"] = get_special_factor("MATERIALS - SLAT", state, special_factors);
        factors["fielders_pricing_factors"]["POLYCARB"] = get_special_factor(
            "MATERIALS - POLYCARBONATE",
            state,
            special_factors
        );
        factors["fielders_pricing_factors"]["CDECKPOLYCARB"] = get_special_factor(
            "MATERIALS - C-Dek 310 POLYCARBONATE",
            state,
            special_factors
        );
        factors["fielders_pricing_factors"]["INFILLS"] = get_special_factor(
            "MATERIALS - INFILL",
            state,
            special_factors
        );
        factors["fielders_pricing_factors"]["SOLARSPAN"] = get_special_factor(
            "MATERIALS - SOLARSPAN",
            state,
            special_factors
        );
        factors["fielders_pricing_factors"]["HARDWARE"] = get_special_factor(
            "MATERIALS - HARDWARE",
            state,
            special_factors
        );
        factors["fielders_pricing_factors"]["BUYINS"] = get_special_factor(
            "MATERIALS - OTHER BUY-INS",
            state,
            special_factors
        );

        let dealer_discount = job?.Distributor?.discount || 0;
        factors["fielders_material_discount_factor"] = dealer_discount ? dealer_discount / 100 : 0;
        factors["fielders_material_markup_overall"] =
            factors["fielders_material_markup_factor"] * (1 - factors["fielders_material_discount_factor"]);

        return factors;
    }

    function GetFactorForDesignType(job, areaIndex, state) {
        const design_type = job.AreaSpecifications[areaIndex][BASIC_SPECS_SECTIONS.SHAPE].DesignType;
        let factor = 1;

        if (design_type === CONFIG.DESIGN_TYPES.FLAT) {
            factor = get_special_factor("FLAT", state, special_factors);
        } else if (design_type === CONFIG.DESIGN_TYPES.GALV_FLAT) {
            factor = get_special_factor("GALV_FLAT", state, special_factors);
        } else if (design_type === CONFIG.DESIGN_TYPES.PITCHED) {
            factor = get_special_factor("PITCHED", state, special_factors);
        } else if (design_type === CONFIG.DESIGN_TYPES.GABLE_PORTAL) {
            factor = get_special_factor("PITCHED", state);
            let end_state = null;

            const shape = job.AreaSpecifications[areaIndex][BASIC_SPECS_SECTIONS.SHAPE];
            if (shape.RoofType1 === "Hip" || shape.RoofType2 === "Hip") end_state = "CARPORT HIP";
            if (shape.RoofType1 === "Dutch Gable" || shape.RoofType2 === "Dutch Gable")
                end_state = "CARPORT DUTCH GABLE";
            if (shape.RoofType1 === "Gazebo" || shape.RoofType2 === "Gazebo") end_state = "CARPORT GAZEBO";
            if (end_state) factor *= get_special_factor(end_state, state, special_factors);

            const sheeting = job.AreaSpecifications[areaIndex][BASIC_SPECS_SECTIONS.ROOF_SHEETING];
            if (sheeting.RoofType.toUpperCase().includes("POLY")) {
                factor *= get_special_factor("CARPORT POLYCARBONATE", state, special_factors);
            }
            if (job.DesignParameters.Freestanding) {
                factor *= get_special_factor("CARPORT FREESTANDING", state, special_factors);
            }
        }

        return factor;
    }

    function get_cust_type(name, dist_levels_param = null) {
        let levels = dist_levels_param || fielders_cust_types;

        if (!Array.isArray(levels) && typeof levels === "object") {
            levels = Object.values(levels);
        }

        if (!Array.isArray(levels)) {
            return 4.05;
        }

        for (const row of levels) {
            if (row.name === name) {
                return Number(row.factor);
            }
        }

        return 4.05;
    }

    function get_special_factor(name, state, special_factors_param = null) {
        let factorsArray = special_factors_default;
        if (special_factors_param)
            factorsArray = Array.isArray(special_factors_param)
                ? special_factors_param
                : Object.values(special_factors_param);

        for (const row of factorsArray) {
            if (row.name === name && row?.[state + "_factors"]) {
                return Number(row?.[state + "_factors"]);
            }
        }
        return 99999;
    }

    const UpdateBracketData = (item, new_bracket_data, bracketData) => {
        let editing_index = bracketData?.editing_index !== undefined ? bracketData?.editing_index : -1;
        let added = false;

        if (item.BracketData) {
            for (let key in item.BracketData) {
                if (item.BracketData[key] && (editing_index === -1 || Number(editing_index) === Number(key))) {
                    added = true;
                    item.BracketData[key]["SideTabLength"] = new_bracket_data["Length 2"];
                    item.BracketData[key]["ftt"] = new_bracket_data["ftt"];
                    if (new_bracket_data["ftt"] === 0 || new_bracket_data["ftt"] === 1)
                        item.BracketData[key]["TopTabLength"] = new_bracket_data["Length 1"];

                    if (new_bracket_data["ftt"] === 0) item.BracketData[key]["TopTabStyle"] = "Upright - Standard";
                    if (new_bracket_data["ftt"] === 1) item.BracketData[key]["TopTabStyle"] = "Upright - Long";
                    if (new_bracket_data["ftt"] === 2) item.BracketData[key]["TopTabStyle"] = "Folded";
                    if (new_bracket_data["ftt"] === 3) item.BracketData[key]["TopTabStyle"] = "None";
                }
            }

            if (!added && editing_index > -1) {
                let key = editing_index;
                item.BracketData[key] = bracketData.BracketData;
                item.BracketData[key]["SideTabLength"] = new_bracket_data["Length 2"];
                item.BracketData[key]["ftt"] = new_bracket_data["ftt"];
                if (new_bracket_data["ftt"] === 0 || new_bracket_data["ftt"] === 1)
                    item.BracketData[key]["TopTabLength"] = new_bracket_data["Length 1"];

                if (new_bracket_data["ftt"] === 0) item.BracketData[key]["TopTabStyle"] = "Upright - Standard";
                if (new_bracket_data["ftt"] === 1) item.BracketData[key]["TopTabStyle"] = "Upright - Long";
                if (new_bracket_data["ftt"] === 2) item.BracketData[key]["TopTabStyle"] = "Folded";
                if (new_bracket_data["ftt"] === 3) item.BracketData[key]["TopTabStyle"] = "None";
            }

            item["CL DATA"] = item.BracketData;
        }
        return item;
    };

    return (
        <Paper elevation={20} style={{ width: "100%", height: "100%", display: "flex", flexDirection: "row" }}>
            <div style={{ flex: 1, position: "relative" }}>
                {showEmailFreightPOA && emailContentFreightPOA && poaEmailSubject && (
                    <MessageSupport
                        style={{ display: showEmailFreightPOA ? "flex" : "none" }}
                        show={showEmailFreightPOA}
                        setShowMessageSupport={setShowEmailFreightPOA}
                        messageType={"FREIGHT POA"}
                        defaultMessage={emailContentFreightPOA}
                        subject={poaEmailSubject}
                    />
                )}

                {designBracketMode && (
                    <BracketDesigner
                        bracketDataCollection={bracketData}
                        mode={designBracketMode}
                        onClose={() => setDesignBracketMode("")}
                        onUpdate={(item, new_bracket_data) => {
                            if (!isPopOutRef.current) {
                                const frameWin = iframeRef.current?.contentWindow;
                                if (!frameWin) return;

                                if (editorItemMode) {
                                    let editor_item = frameWin.GetEditorItemState?.();
                                    if (editor_item) {
                                        editor_item = JSON.parse(editor_item);
                                        if (editor_item && editor_item?.["CL ID"] === new_bracket_data?.["BL ID"]) {
                                            editor_item = UpdateBracketData(editor_item, new_bracket_data, bracketData);
                                            sendValue("editor_item", editor_item);
                                        }
                                    }
                                } else {
                                    let stateTxt = frameWin.GetBOMState?.();
                                    let item_lists = stateTxt ? JSON.parse(stateTxt) : null;
                                    if (item_lists?.order_items?.items) {
                                        let order_items = item_lists.order_items.items;
                                        for (let it of order_items) {
                                            if (it?.["CL ID"] === new_bracket_data?.["BL ID"]) {
                                                UpdateBracketData(it, new_bracket_data, bracketData);
                                            }
                                        }
                                        sendValue("order_items", order_items);
                                    }
                                }
                            } else {
                                postToMatMgr({
                                    type: "UpdateBracketFromParent",
                                    payload: { item, new_bracket_data, bracketData, editorItemMode },
                                });
                            }
                            setDesignBracketMode("");
                        }}
                    />
                )}

                {!isPopOutRef.current && (
                    <iframe
                        ref={iframeRef}
                        name="BOM"
                        src={`${BaseURL("/bom/")}?session=${encodeURIComponent(sessionId.current)}`}
                        width="100%"
                        height="100%"
                        onLoad={handleIframeLoad}
                        style={{ border: "none" }}
                        title="Material Manager"
                        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                    />
                )}
            </div>

            {/* Right: resize handle (kept from your original layout) */}
            <div style={{ width: "4px", background: "#CCC", cursor: "e-resize" }} />

            {showDialogFreightPOA && targetProductFreightPOA !== "" && (
                <BSL_Dialog
                    open={true}
                    onClose={() => {
                        setShowDialogFreightPOA(false);
                        setTargetProductFreightPOA("");
                    }}
                    button2Text="Close"
                    button2Click={() => {
                        setShowDialogFreightPOA(false);
                        setTargetProductFreightPOA("");
                    }}
                    button1Text="Save"
                    button1Click={() => {
                        dispatch(updateBOMData(job));
                        quoteParamsFreightPOA[targetProductFreightPOA] = newFreightPOA;
                        dispatch(updateBOMFreightPOA(quoteParamsFreightPOA));
                        setBOMData();
                        setShowDialogFreightPOA(false);
                        setTargetProductFreightPOA("");
                    }}
                >
                    <BSL_TextInput
                        label={`${targetProductFreightPOA} ${
                            poaItem ? "confirmed charge per EA/LM" : "Delivery Charge"
                            }`}
                        style={{ width: "240px", color: "white" }}
                        helperText={
                            poaItem
                                ? "(0 will unset the price)"
                                : "(0 will reset to the original cost for non-POA Suburbs/Postcodes)"
                        }
                        tooltip={poaItem ? "" : "(0 will reset to the original cost for non-POA Suburbs/Postcodes)"}
                        value={newFreightPOA?.Price}
                        min={0}
                        max={9999}
                        autoFocus
                        tight={true}
                        preValidate={"numeric"}
                        onEnter={(e) => {
                            setNewFreightPOA((prev) => ({
                                ...prev,
                                Price: Number(e.target.value ? e.target.value : e),
                            }));
                        }}
                        onChange={(val) => setNewFreightPOA((prev) => ({ ...prev, Price: Number(val) }))}
                    />
                    {user.Role >= 90 && (
                        <BSL_TextInput
                            label={`${poaItem ? "Supplier" : targetProductFreightPOA} Invoice Number`}
                            style={{ width: "240px", color: "white" }}
                            value={newFreightPOA?.Invoice}
                            tight={true}
                            maxLength={25}
                            onEnter={(e) => setNewFreightPOA((prev) => ({ ...prev, Invoice: e.target.value }))}
                            onChange={(val) => setNewFreightPOA((prev) => ({ ...prev, Invoice: val }))}
                        />
                    )}
                </BSL_Dialog>
            )}
        </Paper>
    );
}
